/**
 * ЛИЧНЫЙ КАБИНЕТ — действия над профилем ТЕКУЩЕГО пользователя.
 *
 * Почему отдельный модуль, а не вызовы SDK прямо из ProfileView:
 * те же три причины, что у roomActions.ts — общий ретрай на 429
 * (withRateLimit уже живёт там и переиспользуется), ошибки человеку
 * по-русски (humanError), компонент остаётся про UI.
 *
 * ГРАНИЦА: здесь только операции над СОБСТВЕННЫМ профилем и чтение
 * собственных устройств/файлов. Чужие профили не трогаем.
 *
 * ЗАМЕР ПО СЕРВЕРУ (важно для честных состояний):
 *  - наш Synapse отдаёт media ТОЛЬКО по авторизованным эндпоинтам
 *    (matrix/media.ts): поэтому аватар показываем через useAuthedMedia
 *    (blob:-URL), а не голым <img src=http>;
 *  - API «покажи все мои файлы» у Synapse НЕТ (README, раздел
 *    «Функциональность» п.2: нужен свой серверный индексатор, его пока
 *    нет). Поэтому «Мои файлы» и «поделились со мной» собираются из
 *    ЗАГРУЖЕННЫХ таймлайнов — честное ограничение, и оно написано в UI,
 *    а не замаскировано под полноту.
 */
import { EventType, MsgType, type MatrixClient, type MatrixEvent } from "matrix-js-sdk";
import { tf, tr } from "../i18n";

import { config } from "../config";
import { ensureJpegAvatar } from "./avatarImage";
import { withRateLimit } from "./roomActions";

/** Ключ localStorage — момент первого входа ЭТОЙ установки (см. deviceCreatedTs). */
const LS_FIRST_LOGIN = "alg.installation.first_login_ms";

/** Ключ localStorage — снимок устройств, уже увиденных кабинетом (для
    оповещения «новое устройство», см. diffNewDevices). */
const LS_KNOWN_DEVICES = "alg.devices.known";

/** Снимок собственного профиля для шапки кабинета. */
export interface OwnProfile {
  userId: string;
  /** Отображаемое имя; null — сервер его не знает (не задано). */
  displayName: string | null;
  /** mxc:// URL аватара; null — аватара нет, рисуем инициалы. */
  avatarMxc: string | null;
}

/**
 * Прочитать собственный профиль.
 *
 * Источник — GET /_matrix/client/v3/profile/{userId} через SDK
 * (getProfileInfo), а НЕ локальное состояние комнат: per-room членство
 * может отличаться от глобального профиля, а кабинет показывает именно
 * глобальный.
 *
 * Подписки на живое обновление здесь нет намеренно: кабинет — экран,
 * который открыли, посмотрели, закрыли. Перечитывание происходит при
 * каждом открытии (см. ProfileView).
 */
export async function fetchOwnProfile(client: MatrixClient): Promise<OwnProfile> {
  const userId = client.getUserId();
  if (!userId) throw new Error(tr("ui.net_userid_klienta"));
  const info = await client.getProfileInfo(userId);
  return {
    userId,
    displayName: typeof info.displayname === "string" ? info.displayname : null,
    avatarMxc: typeof info.avatar_url === "string" ? info.avatar_url : null,
  };
}

/**
 * Изменить отображаемое имя.
 *
 * Пустая строка НЕ допускается вызывающим кодом (UI гасит кнопку):
 * setDisplayName("") на Synapse означает «сбросить имя в MXID», что
 * выглядело бы как «имя пропало» — такое решение человек должен
 * принять явно, и в кабинете его нет (список действий закрыт).
 */
export async function setOwnDisplayName(
  client: MatrixClient,
  name: string,
): Promise<void> {
  await withRateLimit(() => client.setDisplayName(name));
}

/**
 * Установить аватар из файла.
 *
 * Два шага, как у setRoomAvatar в roomActions.ts: uploadContent -> mxc://,
 * затем setAvatarUrl. Если сервер не принял загрузку, профиль не меняется —
 * половинчатого состояния «загрузили, но не применили» быть не должно…
 * на самом деле оно возможно, если второй вызов упадёт: файл останется в
 * media-репозитории сиротой. Это безвредно (сирота не видна никому) и
 * чинится повторной установкой — поэтому отдельной уборки нет.
 *
 * ТОЛЬКО JPEG (2026-09-26). PNG-аватар приходил в push на iOS монограммой
 * вместо картинки; замена того же профиля на JPEG починила уведомления без
 * пересборки клиента. `ensureJpegAvatar` перекодирует что может и отказывает
 * на остальном — тип определяется по первым байтам, а не по имени файла и не
 * по `file.type` (оба подделываются и бывают пустыми). Проверка стоит ДО
 * `uploadContent`: не-JPEG не должен попасть даже в media-репозиторий.
 */
export async function setOwnAvatar(client: MatrixClient, file: File): Promise<void> {
  const jpeg = await ensureJpegAvatar(file);
  const up = await client.uploadContent(jpeg, {
    name: jpeg.name,
    // Тип берём константой, а не из File: он уже ИЗМЕРЕН по сигнатуре.
    type: "image/jpeg",
  });
  await withRateLimit(() => client.setAvatarUrl(up.content_uri));
}

/**
 * Одна запись из GET /devices = один device_id (техническая сессия
 * входа). Одно ФИЗИЧЕСКОЕ устройство («машина + клиент») может быть
 * представлено НЕСКОЛЬКИМИ такими записями: очистка localStorage,
 * гостевой профиль браузера, повторный вход с новым device_id. Сверху
 * их сворачивает groupDevices() по display_name — см. ниже.
 */
export interface DeviceInfo {
  deviceId: string;
  /** Имя, которое человек дал устройству; null — безымянное. */
  displayName: string | null;
  /** IP последней активности, если сервер его отдаёт. */
  lastSeenIp: string | null;
  /** Время последней активности, мс; null — сервер не знает. */
  lastSeenTs: number | null;
  /**
   * Время первого входа устройства, мс — только для ТЕКУЩЕГО (нашей
   * установки). Источник — момент первого запуска приложения на этой
   * машине (localStorage), а НЕ сервер: /devices даты регистрации
   * устройства не отдаёт, и выдумывать её нельзя. У чужих устройств
   * в списке — null, и UI говорит об этом прямо.
   */
  createdTs: number | null;
}

/**
 * Момент первого входа ЭТОЙ установки.
 *
 * Первый вызов — фактически первый запуск приложения после этой правки:
 * пишем «сейчас» и отдаём его. При ближайшем релогине это значение
 * уже совпадёт с реальным первым входом. Для установок, вошедших ДО
 * появления ключа, первый вызов честно покажет «сегодня» — не идеал,
 * но это не выдумка сервера, а измеренный нами факт первого запуска
 * новой версии, и текст в UI («добавлено» только у этого устройства)
 * это отражает.
 */
export function deviceCreatedTs(): number {
  const raw = localStorage.getItem(LS_FIRST_LOGIN);
  const parsed = raw ? Number(raw) : NaN;
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  const now = Date.now();
  try {
    localStorage.setItem(LS_FIRST_LOGIN, String(now));
  } catch {
    /* переполненный/запрещённый localStorage: дата будет «сейчас»
       на каждом заходе — деградация заметная, но не ломающая. */
  }
  return now;
}

/**
 * Имена устройств известных «клиентов» для показа человеку
 * (Антон: «MacBook · Safari»). Распознаём по UA ЭТОГО браузера —
 * это описание НАШЕЙ установки, а не разведка чужих: для записей
 * /devices чужих машин у нас нет ни UA, ни права гадать, поэтому
 * их подпись клиента — «клиент неизвестен» (см. deviceLabel).
 */
function currentMachineLabel(): string {
  const ua = navigator.userAgent;
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return tr("ui.ustroystvo_2");
}

function currentClientLabel(): string {
  const ua = navigator.userAgent;
  // Порядок важен: Edge и Opera содержат «Chrome», а Chrome содержит
  // «Safari» — проверяем от частного к общему.
  if (/Edg(e|A|iOS)?\//.test(ua)) return "Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/YaBrowser\//.test(ua)) return tr("ui.yandeks_brauzer");
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua)) return "Safari";
  return tr("ui.brauzer");
}

/**
 * Человеческая подпись устройства: «Mac · Chrome (веб)».
 *
 * ГРАНИЦА ЧЕСТНОСТИ: и машина, и клиент определены по UA ЭТОГО
 * браузера, поэтому подпись применима только к текущему устройству
 * (isCurrent). Для остальных записей «клиент неизвестен» — сервер
 * его не отдаёт, а угадывать по IP/имени было бы выдумкой.
 *
 * «(веб)», а не просто «Chrome»: desktop-приложение на том же движке
 * дало бы тот же UA-слой, и Антон требует различать MacBook/Safari и
 * MacBook/десктоп как ДВА устройства. Наш клиент — веб, это факт.
 */
export function currentDeviceLabel(): string {
  return tf("profile.deviceLabelWeb", {
    machine: currentMachineLabel(),
    client: currentClientLabel(),
  });
}

/**
 * Серверный display_name, который НАШ клиент задаёт устройству при входе
 * (требование Антона + правило склейки Момо 2026-09-12).
 *
 * Раньше «Mac · Chrome (веб)» (currentDeviceLabel) — подпись для ПОКАЗА в
 * кабинете, где важен браузер («Chrome»), чтобы различать Safari vs
 * десктоп. Но display_name, который ложится НА СЕРВЕР, должен называть
 * ПРОДУКТ, а не движок рендера: склейка устройств идёт по display_name
 * (см. groupDevices), и если бы мы писали «Chrome», то вход тем же
 * клиентом из другого браузера на той же машине дал бы ДРУГОЙ display_name
 * и распался бы на две группы без нужды.
 *
 * Поэтому: «{машина} · {приложение} (веб)» — например
 * «Mac · alg(success) (веб)». Машина — факт из UA ЭТОГО браузера, без
 * гадания модели («MacBook» против «iMac» UA не различает).
 */
export function serveDeviceDisplayName(): string {
  return tf("profile.deviceLabelWeb", {
    machine: currentMachineLabel(),
    client: config.appName,
  });
}

/**
 * Список моих устройств (GET /_matrix/client/v3/devices).
 *
 * ПОСТАНОВКА АНТОНА 2026-09-12 18:40 + приёмка Момо: устройство =
 * связка «машина + клиент». Одно физическое устройство может быть
 * представлено НЕСКОЛЬКИМИ device_id (очистка localStorage, гостевой
 * профиль, новый вход). Поэтому fetchOwnDevices отдаёт ПЛОСКИЙ список
 * device_id, а сверху в UI их сворачивает groupDevices() по display_name
 * (единственному доступному признаку — Synapse не отдаёт user_agent).
 *
 * ФАКТЫ СЕРВЕРА (разведка 2026-09-12):
 *  - GET /devices отдаёт device_id / display_name / last_seen_ip /
 *    last_seen_ts — этим и пользуемся;
 *  - PUT /devices/{id} (переименование) UIA НЕ требует — реализовано
 *    в renameDevice ниже;
 *  - DELETE /devices/{id}: точное поведение (нужна ли UIA, какой
 *    HTTP-код и тело) НЕ ЗАМЕРЕНО. Поэтому удаление из клиента не
 *    делаем вызовом SDK, а ведём в MAS account management по deep-link
 *    (MSC2965): account_management_uri и org.matrix.device_delete
 *    объявлены сервером (замерено по openid-configuration 2026-09-12)
 *    — см. masDeviceUrl. Сам deep-link экран не проходили — честно
 *    «не проверено», а не «работает»;
 *  - Добавление устройства: сервер поддерживает Device Code Flow
 *    (urn:ietf:params:oauth:grant-type:device_code + живой
 *    device_authorization_endpoint, замерено по openid-configuration
 *    2026-09-12). Полная реализация флоу «войти по коду с экрана
 *    нового устройства» — отдельная задача (нужен свой экран входа);
 *    в кабинете кнопка честно ведёт в MAS и объясняет флоу.
 */
export async function fetchOwnDevices(client: MatrixClient): Promise<DeviceInfo[]> {
  interface RawDevice {
    device_id?: string;
    display_name?: string;
    last_seen_ip?: string;
    last_seen_ts?: number;
  }
  const res = (await client.getDevices()) as { devices?: RawDevice[] };
  const me = client.getDeviceId() ?? null;
  const list = (res.devices ?? [])
    .filter((d): d is RawDevice & { device_id: string } =>
      typeof d.device_id === "string" && d.device_id.length > 0,
    )
    .map((d) => ({
      deviceId: d.device_id,
      displayName: typeof d.display_name === "string" ? d.display_name : null,
      lastSeenIp: typeof d.last_seen_ip === "string" ? d.last_seen_ip : null,
      lastSeenTs: typeof d.last_seen_ts === "number" ? d.last_seen_ts : null,
      createdTs: d.device_id === me ? deviceCreatedTs() : null,
    }));
  // Сначала ТЕКУЩЕЕ устройство (человек должен найти себя первым), затем
  // по убыванию активности; безымянные и давно неактивные — в хвост.
  return list.sort((a, b) => {
    if (a.deviceId === me) return -1;
    if (b.deviceId === me) return 1;
    return (b.lastSeenTs ?? 0) - (a.lastSeenTs ?? 0);
  });
}

/**
 * Группа «машина + клиент» — верхний уровень раздела «Устройства».
 *
 * Одно физическое устройство человека может быть представлено в /devices
 * НЕСКОЛЬКИМИ device_id, если человек входил не один раз с разными
 * хранилищами (очистка localStorage, гостевой профиль браузера, разные
 * вкладки с разным устройством) или если сервер выдал новую установку.
 * Антон требует: сверху ОДНА запись на связку «машина + клиент», а
 * сессии (отдельные device_id) — внутри по клику.
 *
 * ПЕРЕДЕЛАНО 25.09 — СКЛЕЙКА ПО display_name УБРАНА.
 *
 * Антон 25.09 20:37 (голосом): «количество сессий меня вообще не
 * интересует, как рисует матрикс». Единица показа — УСТРОЙСТВО.
 *
 * ПОЧЕМУ СКЛЕЙКА ПО ИМЕНИ БЫЛА ВРЕДНА. Она сваливала записи с
 * одинаковым display_name в одну группу и печатала «· N сессий» —
 * ровно те подписи («3 sessions», «2 sessions»), которые Антон прислал
 * скриншотом 25.09 20:43 и потребовал убрать. Хуже того, она
 * СОЗДАВАЛА на экране сущность «сессия» там, где сервер её не даёт.
 *
 * ЗАМЕР БОЕВОГО СЕРВЕРА 25.09 20:44 (Synapse `devices`, @ab, без ключей
 * подписи): РОВНО ОДНА строка `LNCIfoHrRW`. MAS в тот же момент:
 * 1 активная oauth2-сессия из 44 всего. То есть Synapse УЖЕ отдаёт
 * только живые устройства — завершённые сессии из /devices уходят
 * сами. Группировать там нечего.
 *
 * ПРОВЕРЕНО НА ЖИВЫХ ДАННЫХ (Момо, Synapse Admin API 2026-09-12):
 * 7 записей @ab — это следы Element (сторонний клиент), а не наш
 * мессенджер; display_name у всех присутствует, и группировка по нему
 * сворачивает 7 сырых device_id в 4 устройства («Element X on iPhone
 * Air» ×2, «…iPhone 16 Pro Max» ×2, «…iPad Pro M5» ×2, Element Desktop
 * ×1). Правило Момо «display_name, при пустом — нормализованный UA
 * (клиент+модель, БЕЗ версии клиента и ОС)» относится к СЕРВЕРНОЙ
 * склейке (там есть user_agent); в клиенте UA нет, поэтому здесь
 * склейка — строго по display_name, а безымянные не сливаем.
 *
 * ОГРАНИЧЕНИЯ (честно, не скрываем):
 *  - два разных устройства, которым человек дал ОДИНАКОВОЕ имя (или
 *    которым MAS дал одинаковое имя по совпавшему UA), склеятся в одну
 *    группу. Это ложное слияние, но распознать его без user_agent
 *    нельзя — сервер его не отдаёт;
 *  - безымянные записи (display_name null) НЕ склеиваем между собой:
 *    каждая остаётся отдельной группой «без имени», иначе мы слили бы
 *    разные машины в одну.
 *
 * Итог: для текущего состояния Антона (одна установка, одно имя) всегда
 * будет ОДНА группа верхнего уровня — критерий приёмки выполняется; при
 * появлении desktop-приложения (новый device_id, новое имя) — две.
 */
export interface DeviceGroup {
  /** Имя группы = display_name (или null для «без имени»). */
  displayName: string | null;
  /** device_id в этой группе (обычно 1, может быть больше). */
  devices: DeviceInfo[];
}

/**
 * Свернуть плоский список /devices в группы «машина + клиент».
 *
 * Правило: одинаковый непустой display_name -> одна группа; null-имена
 * и уникальные имена -> каждая своя группа. currentDevice сохраняет
 * свою группу-позицию через флаг isCurrent (в DeviceInfo нет поля,
 * поэтому текущее определяем по deviceId === client.getDeviceId()).
 */
export function groupDevices(devices: DeviceInfo[]): DeviceGroup[] {
  // ОДИН `device_id` = ОДНО УСТРОЙСТВО = ОДНА СТРОКА.
  //
  // ПОРЯДОК ВХОДНОГО СПИСКА СОХРАНЯЕТСЯ: fetchOwnDevices уже поставил
  // текущее устройство первым и отсортировал остальные по активности.
  return devices.map((d) => ({ displayName: d.displayName, devices: [d] }));
}

/**
 * Переименовать устройство (PUT /_matrix/client/v3/devices/{deviceId}).
 *
 * UIA для переименования НЕ требуется (замер 2026-09-12). Удаление
 * (DELETE) отдельно НЕ ЗАМЕРЯЛИ — не утверждаем про его UIA. Пустое
 * имя сервер трактует как «сбросить имя» — UI это допускает осознанно
 * (кнопка «Сбросить» отдельная, не случайный пустой ввод).
 */
export async function renameDevice(
  client: MatrixClient,
  deviceId: string,
  displayName: string,
): Promise<void> {
  await withRateLimit(() =>
    client.setDeviceDetails(deviceId, { display_name: displayName }),
  );
}

/* ------------------------------------------------------------------------
 * «НОВОЕ УСТРОЙСТВО» — видимое оповещение (требование Антона 2026-09-12).
 *
 * Механизм честный и простой: кабинет помнит снимок device_id, который
 * человек уже ВИДЕЛ (localStorage). Разница «есть на сервере — не было
 * в снимке» = новые устройства. Это НЕ push-канал: оповещение появляется
 * при открытии раздела, а не в ту же секунду, когда вошли с другого
 * устройства — и UI говорит об этом прямо.
 *
 * Почему не m.devices / push rules: cross-device push про новые логины
 * в нашем стеке не замерен, а выдумывать нельзя (жёсткое правило).
 * --------------------------------------------------------------------- */

/** Прочитать снимок уже увиденных device_id. */
function readKnownDevices(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KNOWN_DEVICES);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

/**
 * Разница «серверный список − увиденный снимок».
 *
 * null-первый-заход (снимка ещё не было) НЕ считается «все устройства
 * новые»: человек только открыл раздел, и баннер «у вас 7 новых
 * устройств» был бы шумом, а не сигналом. Первый снимок просто
 * фиксируется, оповещение начнёт работать со следующего сравнения.
 */
export function diffNewDevices(current: DeviceInfo[]): {
  fresh: DeviceInfo[];
  hadSnapshot: boolean;
} {
  const hadSnapshot = localStorage.getItem(LS_KNOWN_DEVICES) !== null;
  const known = readKnownDevices();
  const fresh = hadSnapshot ? current.filter((d) => !known.has(d.deviceId)) : [];
  return { fresh, hadSnapshot };
}

/**
 * Зафиксировать текущий список как «увиденный». Вызывается, когда
 * человек открыл раздел и баннер показан — увиденное перестаёт быть
 * новым при следующем сравнении.
 */
export function markDevicesSeen(current: DeviceInfo[]): void {
  try {
    localStorage.setItem(
      LS_KNOWN_DEVICES,
      JSON.stringify(current.map((d) => d.deviceId)),
    );
  } catch {
    /* localStorage недоступен: оповещение будет показываться на каждом
       заходе — заметная, но не молчаливая деградация. */
  }
}

/**
 * URL MAS account management для действия над устройством.
 *
 * ЗАМЕР (openid-configuration, 2026-09-12):
 *   account_management_uri = https://mas.algsuccess.com/account/
 *   account_management_actions_supported включает
 *     org.matrix.devices_list, org.matrix.device_view,
 *     org.matrix.device_delete, org.matrix.session_end и др.
 *
 * MSC2965 (account management deep-link) определяет ссылку вида
 *   {account_management_uri}?action=org.matrix.device_delete&device_id=…
 * Сам факт, что MAS объявляет action в actions_supported, означает, что
 * MAS принимает такой deep-link. Конкретный экран, который откроется,
 * мы в браузере НЕ проходили — поэтому кнопка честно называется
 * «Открыть управление устройством», а не «Удалить сейчас».
 */
export function masDeviceUrl(action: string, deviceId?: string): string {
  const base = "https://mas.algsuccess.com/account/";
  const q = new URLSearchParams({ action });
  if (deviceId) q.set("device_id", deviceId);
  return `${base}?${q.toString()}`;
}

/* ------------------------------------------------------------------------
 * УДАЛЕНИЕ УСТРОЙСТВА ПО-НАСТОЯЩЕМУ (задача 2026-09-13).
 *
 * СТАРЫЙ ПОДХОД (до этой правки): кнопка удаления вела deep-link'ом в
 * MAS account management (masDeviceUrl выше) — уход из приложения в
 * браузер. Антон: «в списке устройств нет удалить устройство» — формально
 * кнопка была, но вела наружу и вдобавок была скрыта у текущего
 * устройства, а у Антона оно единственное — кнопки он не видел ВООБЩЕ.
 *
 * НОВЫЙ ПОДХОД: удаляем САМИ, не уходя из приложения.
 *
 * КАК РАБОТАЕТ УДАЛЕНИЕ (два механизма, один интерфейс):
 *
 * 1. COMPAT-СЕССИЯ (legacy Matrix login API — наш случай до перехода
 *    на чистый OIDC): вызываем MAS Internal GraphQL API
 *    (org.matrix.matrix-authentication-service.graphql_endpoint из
 *    openid-configuration). Мутация endCompatSession завершает сессию
 *    по её ULID. Проблема: client.getDevices() отдаёт device_id
 *    (например "ALGW1A2B3C4D"), а GraphQL оперирует ULID сессии.
 *    Мостик — запрос viewer { ... } не отдаёт compat-сессий по
 *    device_id напрямую, поэтому идём другим путём: получаем список
 *    ВСЕХ compat-сессий пользователя (Admin API) и ищем по device_id.
 *    НО Admin API требует scope urn:mas:admin, которого у обычного
 *    пользователя нет. Поэтому:
 *
 * 2. РАБОЧИЙ ПУТЬ ДЛЯ РЯДОВОГО ПОЛЬЗОВАТЕЛЯ: MAS GraphQL API с scope
 *    urn:mas:graphql:* (любой пользователь может его запросить — см.
 *    reference/scopes.html: "The default policy allows any client and
 *    any user to request this scope"). Авторизованные как пользователь
 *    (без urn:mas:admin) запросы видят только СВОИ данные. Мутация
 *    endCompatSession / endOauth2Session завершает СВОЮ сессию.
 *
 *    Проблема мостика device_id → session ULID остаётся. Решаем её
 *    GraphQL-запросом: viewer { browserSessions { appSessions } } —
 *    но это сложно. ПРОЩЕ: сначала пытаемся удалить через C-S API
 *    DELETE /_matrix/client/v3/devices/{deviceId} (который для OAuth2
 *    сессий работает БЕЗ UIA — MSC3861). Если сервер отвечает 401 с
 *    требованием UIA (compat-сессия) — тогда идём в MAS GraphQL.
 *
 * РЕАЛИЗАЦИЯ НИЖЕ: пробуем C-S API, при 401+UIA — MAS GraphQL
 * endCompatSession (для compat) или endOauth2Session (для OAuth2).
 * Текущее устройство удалять можно — это честный выход из аккаунта,
 * и UI предупреждает об этом перед действием.
 * --------------------------------------------------------------------- */

/** Ответ openid-configuration (кэшируем на сессию). */
interface OidcDiscovery {
  device_authorization_endpoint?: string;
  token_endpoint?: string;
  ["org.matrix.matrix-authentication-service.graphql_endpoint"]?: string;
  account_management_uri?: string;
}

let cachedDiscovery: OidcDiscovery | null = null;

async function fetchDiscovery(): Promise<OidcDiscovery> {
  if (cachedDiscovery) return cachedDiscovery;
  const res = await fetch("https://mas.algsuccess.com/.well-known/openid-configuration");
  if (!res.ok) throw new Error(`OIDC discovery failed: HTTP ${res.status}`);
  cachedDiscovery = (await res.json()) as OidcDiscovery;
  return cachedDiscovery;
}

/** Получить access token текущей сессии из SDK. */
function getAccessToken(client: MatrixClient): string {
  // getAccessToken — публичный метод MatrixClient.
  const token = client.getAccessToken();
  if (!token) throw new Error(tr("ui.net_access_token"));
  return token;
}

/** Получить client_id текущей сессии из localStorage (сохранён при логине). */
function getClientId(): string {
  const raw = localStorage.getItem("alg.oidc.client_id");
  if (!raw) throw new Error(tr("ui.net_client_id_sessiya_ne_oidc"));
  return raw;
}

/** Получить device_id текущей сессии. */
function getDeviceId(client: MatrixClient): string {
  const id = client.getDeviceId();
  if (!id) throw new Error(tr("ui.net_device_id"));
  return id;
}

/* ------------------------------------------------------------------------
 * УДАЛЕНИЕ УСТРОЙСТВА ЧЕРЕЗ C-S API + MAS GraphQL FALLBACK
 * --------------------------------------------------------------------- */

/**
 * Удалить устройство ПО-НАСТОЯЩЕМУ, не уходя из приложения.
 *
 * ╔═════════════════════════════════════════════════════════════════╗
 * ║ ПОЧЕМУ УДАЛЕНИЕ НЕ РАБОТАЛО (Антон 25.09: «через сессии       ║
 * ║ элемента не удаляются»). Замер по ИСХОДНИКАМ Synapse 25.09:   ║
 * ║                                                                    ║
 * ║ synapse/rest/client/devices.py:185 (DeviceRestServlet.on_DELETE):  ║
 * ║     if self._auth_delegation_enabled:                              ║
 * ║         raise UnrecognizedRequestError(code=404)                    ║
 * ║                                                                    ║
 * ║ Где _auth_delegation_enabled = hs.config.mas.enabled, а у нас в    ║
 * ║ homeserver.yaml стоит matrix_authentication_service.enabled: true. ║
 * ║                                                                    ║
 * ║ ТО ЕСТЬ: при делегировании авторизации в MAS эндпоинт        ║
 * ║ DELETE /_matrix/client/v3/devices/{id} ОТКЛЮЧЁН ЦЕЛИКОМ и отвечает  ║
 * ║ 404 M_UNRECOGNIZED ВСЕГДА — для ЛЮБОГО устройства, нашего или ║
 * ║ чужого. Дело НЕ в Element X и НЕ в типе сессии.            ║
 * ║                                                                    ║
 * ║ СТАРЫЙ КОД ЖДАЛ НЕ ТОГО КОДА ОШИБКИ: фоллбэк в MAS         ║
 * ║ включался только на 401+flows (UIA). На 404 старый код делал     ║
 * ║ throw и до MAS НЕ ДОХОДИЛ НИКОГДА. Именно это видел Антон.  ║
 * ║ (401+UIA в нашем стеке невозможен в принципе:              ║
 * ║ handlers/auth.py:331 при делегации кидает 500 «UIA shouldn't be   ║
 * ║ used when auth is delegated», то есть условие было мёртвым.)    ║
 * ║                                                                    ║
 * ║ ВТОРАЯ ОШИБКА — в самом фоллбэке: он шёл через            ║
 * ║ browserSessions -> appSessions и сравнивал s.deviceId, который    ║
 * ║ есть ТОЛЬКО у CompatSession. У Oauth2Session (все входы Element X  ║
 * ║ и нашего клиента) поля deviceId нет — device_id зашит в scope.  ║
 * ║ Старый код их явно ПРОПУСКАЛ (см. его же комментарий) и     ║
 * ║ падал с «session not found». Замер MAS 25.09: у @ab 38 активных   ║
 * ║ oauth2_sessions и всего 1 compat — то есть фоллбэк не сработал   ║
 * ║ бы практически ни для одной записи, даже если бы дошёл.    ║
 * ╚═════════════════════════════════════════════════════════════════╝
 *
 * СТАЛО: единственный рабочий путь — MAS GraphQL. C-S API оставлен
 * ПЕРВОЙ попыткой (стенд без делегации или будущее снятие MSC3861
 * должны продолжить работать), но его отказ больше НЕ фатален:
 * на 404/401/любой другой отказ уходим в MAS.
 *
 * Удаление ТЕКУЩЕГО устройства = выход из аккаунта. После успешного
 * удаления текущего device_id клиент перестаёт быть авторизованным,
 * и App должен перейти на экран входа. UI обязан предупредить человека
 * об этом ДО вызова.
 */
export async function deleteDevice(
  client: MatrixClient,
  deviceId: string,
): Promise<void> {
  const isCurrent = deviceId === getDeviceId(client);

  // Попытка 1: C-S API DELETE /devices/{deviceId}.
  // ПРИ ДЕЛЕГАЦИИ В MAS ЭТОТ ЭНДПОИНТ ВСЕГДА ОТДАЁТ 404 (см. рамку
  // выше). Оставлен ради конфигураций БЕЗ делегации, где он работает.
  try {
    await withRateLimit(() => client.deleteDevice(deviceId));
    if (isCurrent) {
      // Удалили СЕБЯ: сессия мертва, чистим localStorage, чтобы
      // createSyncedClient не пытался воскресить её.
      localStorage.removeItem("alg.session");
      localStorage.removeItem("alg.oidc.client_id");
      localStorage.removeItem("alg.installation.device_id");
    }
    return;
  } catch {
    // ЛЮБОЙ отказ C-S API — не конец, а переход к MAS. Раньше здесь
    // стояло узкое условие «только 401+flows», и именно оно хоронило
    // удаление на 404. Ошибку не глотаем: если и MAS откажет,
    // исключение улетит наружу из deleteViaMasGraphql ниже.
  }

  // Попытка 2: MAS GraphQL — единственный рабочий путь в нашем стеке.
  await deleteViaMasGraphql(client, deviceId, isCurrent);
}

/**
 * Завершение сессии устройства через MAS Internal GraphQL API.
 *
 * РАБОТАЕТ ДЛЯ ОБОИХ ТИПОВ СЕССИЙ, включая Element X.
 *
 * БЫЛО (сломано): обход browserSessions -> appSessions с ручным
 * сравнением s.deviceId. Поле deviceId есть ТОЛЬКО у CompatSession,
 * поэтому все Oauth2Session (т.е. Element X и наш собственный клиент)
 * молча пропускались и функция падала «session not found».
 *
 * СТАЛО: штатный фильтр схемы MAS — User.appSessions(device: "…").
 * Замер схемы живого MAS 1.24.0 (introspection 25.09):
 *   appSessions(state: SessionState, device: String, lastActive: DateFilter,
 *               browserSession: ID, after/before/first/last)
 *   «Get the list of both compat and OAuth 2.0 sessions»;
 *   аргумент device — «List only sessions for the given device.»;
 *   union AppSession = CompatSession | Oauth2Session.
 * То есть сервер сам умеет сопоставлять device_id с OAuth2-scope — в клиенте
 * разбирать scope руками не надо и НЕЛЬЗЯ — и это не вопрос вкуса.
 *
 * ПОЧЕМУ РУЧНОЙ РАЗБОР SCOPE БЫЛ БЫ ОШИБКОЙ. Замер базы MAS 25.09
 * (активные oauth2_sessions, 71 из 127 со scope-токеном устройства):
 *   urn:matrix:client:device:<ID>                      — 41 шт.
 *   urn:matrix:org.matrix.msc2967.client:device:<ID>   — 30 шт.
 * Живут ОБЕ формы сразу (стабильная и старая MSC2967). Клиент, сравнивающий
 * с одной строкой, тихо промахнулся бы примерно на трети сессий.
 * Сервер же проверяет обе: storage-pg/src/oauth2/session.rs:145-159
 * — `if let Ok([stable_scope_token, unstable_scope_token]) = device.to_scope_token()`.
 *
 * Тип сессии берём из __typename ответа и по нему выбираем мутацию
 * (endCompatSession / endOauth2Session) — обе есть в схеме, замерено.
 */
async function deleteViaMasGraphql(
  client: MatrixClient,
  deviceId: string,
  isCurrent: boolean,
  tolerateMissing = false,
): Promise<void> {
  const discovery = await fetchDiscovery();
  const graphqlUrl = discovery["org.matrix.matrix-authentication-service.graphql_endpoint"];
  if (!graphqlUrl) throw new Error(tr("ui.mas_graphql_endpoint_ne_obyavlen"));

  const token = getAccessToken(client);

  // Шаг 1: найти АКТИВНУЮ сессию именно этого device_id.
  // device_id передаём ПЕРЕМЕННОЙ, а не склейкой строки: значение
  // приходит с сервера и не должно попадать в тело запроса напрямую.
  interface AppSessionNode {
    id: string;
    __typename: string;
  }
  const findQuery = `
    query FindSessionByDevice($device: String!) {
      viewer {
        ... on User {
          appSessions(first: 10, state: ACTIVE, device: $device) {
            nodes {
              __typename
              ... on CompatSession { id }
              ... on Oauth2Session { id }
            }
          }
        }
      }
    }
  `;

  const findRes = await fetch(graphqlUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query: findQuery, variables: { device: deviceId } }),
  });

  if (!findRes.ok) {
    throw new Error(`MAS GraphQL: HTTP ${findRes.status}`);
  }

  const findData = (await findRes.json()) as {
    data?: {
      viewer?: {
        appSessions?: { nodes: AppSessionNode[] };
      };
    };
    errors?: { message: string }[];
  };

  if (findData.errors?.length) {
    throw new Error(`MAS GraphQL: ${findData.errors[0].message}`);
  }

  const nodes = findData.data?.viewer?.appSessions?.nodes ?? [];
  const node = nodes.find(
    (n) => n.__typename === "CompatSession" || n.__typename === "Oauth2Session",
  );

  if (!node) {
    // ВЫХОД ИЗ СВОЕГО АККАУНТА: активной сессии уже нет — истекла,
    // завершена с другого устройства или прошлой попыткой выхода.
    // Для УДАЛЕНИЯ чужого устройства это ошибка: человек просил
    // убрать конкретную строку и вправе узнать, что не вышло.
    // Для ВЫХОДА это уже достигнутая цель: сервер сессию не держит,
    // осталось стереть локальное. Запереть человека в аккаунте
    // из-за отсутствия того, что мы и так собирались убить, нельзя.
    if (tolerateMissing) return;
    throw new Error(tf("profile.masSessionNotFound", { deviceId }));
  }

  const sessionId = node.id;
  const isCompat = node.__typename === "CompatSession";

  // Шаг 2: завершить сессию. ULID тоже идёт переменной, а не вклейкой
  // в текст запроса: так значение с сервера не может повлиять на
  // структуру GraphQL-документа.
  const mutation = isCompat
    ? `mutation EndCompat($id: ID!) { endCompatSession(input: { compatSessionId: $id }) { status } }`
    : `mutation EndOauth2($id: ID!) { endOauth2Session(input: { oauth2SessionId: $id }) { status } }`;

  const mutRes = await fetch(graphqlUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query: mutation, variables: { id: sessionId } }),
  });

  if (!mutRes.ok) {
    throw new Error(`MAS GraphQL mutation: HTTP ${mutRes.status}`);
  }

  const mutData = (await mutRes.json()) as {
    data?: { endCompatSession?: { status: string }; endOauth2Session?: { status: string } };
    errors?: { message: string }[];
  };

  if (mutData.errors?.length) {
    throw new Error(`MAS GraphQL mutation: ${mutData.errors[0].message}`);
  }

  const status = mutData.data?.endCompatSession?.status ?? mutData.data?.endOauth2Session?.status;
  if (status !== "ENDED" && status !== "NOT_FOUND") {
    throw new Error(tf("profile.deviceDeleteUnexpectedStatus", { status: String(status) }));
  }

  if (isCurrent) {
    localStorage.removeItem("alg.session");
    localStorage.removeItem("alg.oidc.client_id");
    localStorage.removeItem("alg.installation.device_id");
    // Своя сессия мертва — спрашивать Synapse больше нечем и незачем.
    return;
  }

  // Шаг 3: ДОЖДАТЬСЯ, ПОКА УСТРОЙСТВО ПРОПАДЁТ ИЗ СПИСКА SYNAPSE.
  //
  // ЗАМЕР ИСХОДНИКОВ MAS 1.24.0 (25.09,
  // crates/handlers/src/graphql/mutations/oauth2_session.rs:285-291):
  //     // Schedule a job to sync the devices of the user with the homeserver
  //     repo.queue_job().schedule_job(&mut rng, &clock, SyncDevicesJob::new(&user))
  // То есть endOauth2Session/endCompatSession завершает сессию в MAS
  // СРАЗУ, а устройство из Synapse удаляет ОТЛОЖЕННОЙ ФОНОВОЙ ЗАДАЧЕЙ.
  //
  // ПОЧЕМУ ЭТО ВАЖНО: список в кабинете строится через fetchOwnDevices,
  // то есть C-S GET /devices — это данные SYNAPSE, а не MAS. Экран
  // перечитывает список сразу после успешного удаления. Без ожидания
  // человек в типичном случае снова увидит удалённое устройство на
  // месте — ровно то самое «нажал удалить, ничего не произошло»,
  // только теперь уже ПОСЛЕ фактически успешного удаления.
  //
  // Ждём ограниченно и не считаем таймаут ошибкой: сессия УЖЕ завершена
  // (это подтверждено статусом ENDED), значит входы с устройства больше
  // невозможны. Строка в списке — отставший кэш homeserver, а не
  // неудача. Врать человека об ошибке в этом месте нельзя.
  await waitForDeviceGone(client, deviceId);
}

/**
 * ВЫХОД ИЗ АККАУНТА — ЗАВЕРШЕНИЕ СВОЕЙ СЕССИИ НА СЕРВЕРЕ.
 *
 * ПОЧЕМУ ЭТА ФУНКЦИЯ ПОЯВИЛАСЬ (Антон 25.09 19:19: «Sign out не
 * работает ни в ВЭБе ни на телефоне», «это серьезный баг»).
 * logout() в auth/oidc.ts делал ТОЛЬКО локальную очистку:
 * обнулял activeClient/activeRefresher и звал clearSession().
 * Никакого запроса на сервер не было вообще. Следствие: браузер
 * забывал токен, но СЕССИЯ НА СЕРВЕРЕ ОСТАВАЛАСЬ ЖИВОЙ — устройство
 * продолжало висеть в списке сессий, пуши могли продолжать приходить,
 * а access-токен оставался действительным до самого истечения. Для
 * человека это выглядит ровно как «sign out не работает».
 *
 * ЗАМЕР ЖИВОГО СЕРВЕРА 25.09 (почему путь именно такой):
 *  - POST /_matrix/client/v3/logout     -> 404 M_UNRECOGNIZED
 *  - POST /_matrix/client/v3/logout/all -> 404 M_UNRECOGNIZED
 *    (проверено запросом с заведомо невалидным токеном). Штатный
 *    matrix-логаут при делегации в MAS ОТКЛЮЧЁН — ровно та же болезнь,
 *    что была у DELETE /devices/<id>.
 *  - end_session_endpoint ОТСУТСТВУЕТ и в /_matrix/client/v1/auth_metadata,
 *    и в https://mas.algsuccess.com/.well-known/openid-configuration.
 *    RP-initiated logout нам недоступен, городить его нельзя.
 *  - revocation_endpoint есть, но требует аутентификации клиента
 *    (без client_id -> 400 «No credentials were presented»), и отзывает
 *    ОДИН ТОКЕН, а не сессию: refresh-токен и запись устройства выжили бы.
 *
 * ПОЭТОМУ: единственный честный путь — та же мутация MAS GraphQL,
 * которой уже удаляются устройства (deleteViaMasGraphql). Выход —
 * это завершение СВОЕЙ сессии, то есть тот же код с isCurrent=true.
 *
 * ОТЛИЧИЕ ОТ УДАЛЕНИЯ: отсутствие активной сессии в MAS здесь НЕ ошибка
 * (tolerateMissing=true). Если сессия уже мертва — цель достигнута,
 * человека надо выпустить, а не показывать ему отказ.
 *
 * Функция НЕ трогает pusher и НЕ чистит localStorage сверх того, что
 * делает deleteViaMasGraphql: порядок шагов задаёт вызывающий код
 * (App.tsx), потому что снятие pusher требует ещё живого токена.
 */
export async function endOwnSession(client: MatrixClient): Promise<void> {
  const deviceId = getDeviceId(client);
  await deleteViaMasGraphql(client, deviceId, true, true);
}

/**
 * Ограниченное ожидание, пока Synapse догонит MAS и уберёт device_id.
 *
 * Бюджет намеренно небольшой: фоновая задача MAS отрабатывает быстро,
 * а держать человека перед замершей кнопкой дольше пары секунд хуже,
 * чем показать список с одной отставшей строкой (следующее открытие
 * раздела покажет правду).
 *
 * Ошибки опроса ГЛОТАЮТСЯ осознанно: это проверка, а не действие.
 * Упавший GET /devices не отменяет уже выполненного удаления и не
 * должен превращаться в сообщение об ошибке удаления.
 */
async function waitForDeviceGone(
  client: MatrixClient,
  deviceId: string,
  attempts = 6,
  delayMs = 500,
): Promise<void> {
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const res = (await client.getDevices()) as { devices?: { device_id?: string }[] };
      const stillThere = (res.devices ?? []).some((d) => d.device_id === deviceId);
      if (!stillThere) return;
    } catch {
      // Опрос не удался — выходим молча, см. комментарий выше.
      return;
    }
  }
}

/* ------------------------------------------------------------------------
 * ДОБАВЛЕНИЕ УСТРОЙСТВА — DEVICE CODE FLOW (задача 2026-09-13).
 *
 * СТАРЫЙ ПОДХОД: кнопка «Добавить устройство» вела в MAS account
 * management (window.open). Антон: «нет добавить устройство» — формально
 * кнопка была, но уводила в браузер, и человек её не находил.
 *
 * НОВЫЙ ПОДХОД: свой экран входа по коду (RFC 8628 Device Authorization
 * Grant). Сервер его поддерживает (замер 2026-09-12: device_authorization_endpoint
 * живой, grant urn:ietf:params:oauth:grant-type:device_code объявлен).
 *
 * ФЛОУ:
 * 1. Пользователь на НОВОМ устройстве открывает наш клиент.
 * 2. Клиент вызывает startDeviceCodeFlow() -> получает user_code +
 *    verification_uri.
 * 3. Пользователь на ЭТОМ устройстве (где уже вошёл) вводит код
 *    на экране «Добавить устройство».
 * 4. Этот экран вызывает approveDeviceCode(user_code) -> MAS
 *    авторизует новое устройство.
 * 5. Новое устройство через waitForDeviceCode() получает токен.
 *
 * ВАЖНО: approveDeviceCode — это НЕ стандартный endpoint MAS!
 * MAS предоставляет verification_uri, на котором человек вводит код
 * В БРАУЗЕРЕ. Но мы хотим свой экран. Для этого используем MAS
 * GraphQL: mutation authorizeDeviceCode (если она есть) или прямой
 * POST на verification_uri с кодом.
 *
 * ЗАМЕР (2026-09-13): MAS verification_uri = https://mas.algsuccess.com/device.
 * POST на него с user_code авторизует устройство. Это не GraphQL,
 * а обычная форма. Но у нас нет гарантии, что MAS принимает API-вызов
 * вместо HTML-формы. Поэтому РАБОЧИЙ ПУТЬ:
 *
 * - Свой экран показывает user_code и verification_uri.
 * - Пользователь вводит код у нас.
 * - Мы открываем verification_uri + ?user_code=... в скрытом iframe
 *   или делаем fetch (CORS может блокировать).
 * - Альтернатива: используем MAS GraphQL mutation, если она есть.
 *
 * ПРОВЕРКА СХЕМЫ GraphQL (2026-09-13): мутации для device authorization
 * в schema.graphql НЕТ. Значит, approve идёт через verification_uri.
 *
 * ИТОГОВАЯ РЕАЛИЗАЦИЯ: экран показывает код + ссылку. Код человек
 * вводит на НОВОМ устройстве. Этот экран НЕ вводит код — он ГЕНЕРИРУЕТ
 * его для нового устройства. Ввод кода — на новом устройстве.
 *
 * ВНИМАНИЕ: это означает, что «Добавить устройство» в кабинете
 * — это ГЕНЕРАТОР кода для нового устройства, а не форма ввода.
 * Форма ввода кода должна быть на экране входа (login screen).
 * Но задача Антона: «добавление устройства через свой экран входа
 * по коду». Значит, экран входа должен уметь принимать код.
 *
 * РЕШЕНИЕ: в кабинете «Добавить устройство» открывает экран,
 * который генерирует код и показывает его. На новом устройстве
 * человек открывает клиент, выбирает «Войти по коду», вводит код.
 * Это ДВА экрана: генератор (в кабинете) и ввод (на экране входа).
 * --------------------------------------------------------------------- */

/** Ответ device_authorization_endpoint (RFC 8628). */
export interface DeviceCodeSession {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  verificationUriComplete?: string;
  expiresIn: number;
  interval?: number;
}

/**
 * Начать Device Code Flow: получить код для ввода на новом устройстве.
 *
 * Вызывается на УЖЕ ВОШЕДШЕМ устройстве (кабинет). Код показывается
 * человеку, который вводит его на новом устройстве.
 *
 * scope: запрашиваем полный доступ к C-S API + device_id. device_id
 * генерируем новый (не installationDeviceId — это НОВОЕ устройство).
 */
export async function startDeviceCodeFlow(): Promise<DeviceCodeSession> {
  const discovery = await fetchDiscovery();
  const endpoint = discovery.device_authorization_endpoint;
  if (!endpoint) throw new Error(tr("ui.device_authorization_endpoint_ne_obyavle"));

  const clientId = getClientId();
  // Новый device_id для НОВОГО устройства. Не используем installationDeviceId —
  // тот привязан к этой установке.
  const newDeviceId = "ALGW" + randomHex(6).toUpperCase();
  const scope = `urn:matrix:client:api:* urn:matrix:client:device:${newDeviceId}`;

  const res = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: clientId, scope }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Device authorization failed: HTTP ${res.status} — ${text}`);
  }

  const data = (await res.json()) as {
    device_code: string;
    user_code: string;
    verification_uri: string;
    verification_uri_complete?: string;
    expires_in: number;
    interval?: number;
  };

  return {
    deviceCode: data.device_code,
    userCode: data.user_code,
    verificationUri: data.verification_uri,
    verificationUriComplete: data.verification_uri_complete,
    expiresIn: data.expires_in,
    interval: data.interval,
  };
}

/** Результат ожидания авторизации. */
export interface DeviceCodeResult {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}

/**
 * Ждать, пока пользователь авторизует код на новом устройстве.
 *
 * Вызывается на ЭТОМ устройстве (где показан код). Когда человек
 * на новом устройстве введёт код, MAS выдаст токен, и эта функция
 * его вернёт. Токен нужен НОВОМУ устройству — но мы его получаем
 * ЗДЕСЬ. Передача токена на новое устройство — проблема безопасности.
 *
 * РЕШЕНИЕ: этот экран НЕ ждёт токен. Он просто показывает код.
 * Ожидание токена — на НОВОМ устройстве (waitForDeviceCode там).
 * Здесь waitForDeviceCode вызывается только для проверки, что код
 * ещё жив (опционально).
 *
 * ПРАВИЛЬНЫЙ ФЛОУ (RFC 8628):
 * - Это устройство: startDeviceCodeFlow() -> показать код.
 * - Новое устройство: startDeviceCodeFlow() -> waitForDeviceCode() -> токен.
 * Но тогда код генерируется на новом устройстве, а не здесь.
 *
 * АЛЬТЕРНАТИВА (которую мы реализуем):
 * - Это устройство: startDeviceCodeFlow() -> показать user_code.
 * - Новое устройство: вводит user_code на verification_uri (MAS).
 * - Новое устройство: waitForDeviceCode(session) -> токен.
 *
 * Но session (device_code) известен только ЭТОМУ устройству. Значит,
 * новое устройство должно получить device_code отсюда. Как? QR-код?
 * Ручной ввод? Это сложно.
 *
 * УПРОЩЕНИЕ (для нашего случая): мы делаем ГЕНЕРАТОР кода на этом
 * устройстве. Новое устройство вводит код на MAS verification_uri.
 * Новое устройство НЕ использует наш клиент для входа — оно использует
 * браузер. Но Антон хочет «свой экран входа по коду».
 *
 * ИТОГОВОЕ РЕШЕНИЕ: экран в кабинете генерирует код и показывает его
 * вместе с verification_uri. Человек на новом устройстве открывает
 * verification_uri (в браузере), вводит код, авторизует. Новое
 * устройство получает токен через браузерный флоу (authorization code).
 * Наш клиент на новом устройстве потом логинится обычным OIDC.
 *
 * НО это не «свой экран входа по коду». Для настоящего Device Code
 * Flow нужно, чтобы НОВОЕ устройство тоже было нашим клиентом.
 * РЕАЛИЗУЕМ: на экране входа добавляем кнопку «Войти по коду», которая
 * вызывает startDeviceCodeFlow() и показывает код. Человек на УЖЕ
 * ВОШЕДШЕМ устройстве вводит этот код в кабинете. Тогда:
 * - Новое устройство: startDeviceCodeFlow() -> user_code + device_code
 * - Старое устройство: approveDeviceCode(user_code) -> MAS авторизует
 * - Новое устройство: waitForDeviceCode(device_code) -> токен
 *
 * Для approveDeviceCode нужен endpoint. MAS его не предоставляет как
 * API — только HTML-форму на verification_uri. Поэтому approveDeviceCode
 * делает fetch на verification_uri с user_code. Если CORS блокирует,
 * открываем verification_uri + ?user_code=... в новой вкладке.
 */
export async function waitForDeviceCode(
  session: DeviceCodeSession,
): Promise<DeviceCodeResult> {
  const discovery = await fetchDiscovery();
  const tokenEndpoint = discovery.token_endpoint;
  if (!tokenEndpoint) throw new Error(tr("ui.token_endpoint_ne_obyavlen"));

  const clientId = getClientId();
  const interval = (session.interval ?? 5) * 1000;
  const expiration = Date.now() + session.expiresIn * 1000;

  let currentInterval = interval;
  while (Date.now() < expiration) {
    const res = await fetch(tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        device_code: session.deviceCode,
        grant_type: "urn:ietf:params:oauth:grant-type:device_code",
        client_id: clientId,
      }),
    });

    const data = (await res.json()) as {
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
      error?: string;
    };

    if (res.ok && data.access_token) {
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
      };
    }

    if (data.error === "authorization_pending") {
      // Ждём дальше.
    } else if (data.error === "slow_down") {
      currentInterval += 5000;
    } else if (data.error === "access_denied") {
      throw new Error(tr("ui.polzovatel_otklonil_avtorizaciyu"));
    } else if (data.error === "expired_token") {
      throw new Error(tr("ui.kod_ist_k"));
    }

    await new Promise((r) => setTimeout(r, currentInterval));
  }

  throw new Error(tr("ui.vremya_ozhidaniya_isteklo"));
}

/**
 * Авторизовать код устройства (approve).
 *
 * Вызывается на УЖЕ ВОШЕДШЕМ устройстве. user_code — код, который
 * показало НОВОЕ устройство.
 *
 * MAS verification_uri = https://mas.algsuccess.com/device. Форма
 * на этой странице принимает user_code и авторизует устройство.
 *
 * ЧЕСТНОЕ ОГРАНИЧЕНИЕ (правка Момо 2026-09-13): отдельного API для
 * подтверждения кода у MAS НЕТ. Прежний вариант слал POST в режиме
 * `no-cors` и СЧИТАЛ УСПЕХОМ то, что прочитать невозможно: при таком
 * режиме браузер не отдаёт ни статус, ни тело, поэтому «успех» был
 * выдуманным — подтверждение могло не пройти, а интерфейс показал бы,
 * что всё хорошо. Такой отчёт хуже отсутствия функции.
 *
 * Поэтому подтверждение выполняется на странице MAS, куда код
 * подставляется параметром. Результат подтверждает сервер, а не мы.
 */
export async function approveDeviceCode(userCode: string): Promise<void> {
  const discovery = await fetchDiscovery();
  const base = discovery.account_management_uri?.replace("/account/", "") ?? "https://mas.algsuccess.com";
  const verificationUri = `${base}/device`;
  const url = `${verificationUri}?user_code=${encodeURIComponent(userCode)}`;
  window.open(url, "_blank", "noopener");
}

/** Вспомогательная функция для генерации device_id. */
function randomHex(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Файл/медиа из загруженного таймлайна — для разделов «Мои файлы». */
export interface SharedFile {
  eventId: string;
  roomId: string;
  roomName: string;
  /** MXID отправителя. */
  senderId: string;
  senderName: string;
  ts: number;
  /**
   * Отображаемое имя. У сообщений с подписью (MSC2530) в body лежит
   * ПОДПИСЬ, а имя файла — в filename: тогда name = подпись (её человек
   * и видел в ленте), а filename — настоящее имя файла (см. ниже).
   */
  name: string;
  /**
   * Настоящее имя файла из content.filename, если отправитель его прислал
   * (сообщения с подписью). null — filename не задан, name и есть имя.
   * Нужен поиску: Антон (12.09 18:00) требует искать и по имени файла,
   * и по подписи — без этого поля поиск находил бы только одно из двух.
   */
  filename: string | null;
  mxc: string;
  /** mxc:// серверного превью (info.thumbnail_url), если отправитель его
      вложил; null — превью нет, для сетки запрашиваем /thumbnail сами. */
  thumbMxc: string | null;
  mimetype: string;
  size: number | null;
  /** Картинка/видео/аудио — по msgtype, как в ленте. */
  kind: "image" | "video" | "audio" | "file";
}

const FILE_MSGTYPES = new Set<string>([
  MsgType.Image,
  MsgType.Video,
  MsgType.Audio,
  MsgType.File,
]);

function kindOf(msgtype: string): SharedFile["kind"] {
  if (msgtype === MsgType.Image) return "image";
  if (msgtype === MsgType.Video) return "video";
  if (msgtype === MsgType.Audio) return "audio";
  return "file";
}

/**
 * Собрать файлы из ЗАГРУЖЕННЫХ таймлайнов всех комнат, где я состою.
 *
 * ЧЕСТНОЕ ОГРАНИЧЕНИЕ (README п.2): серверного индекса «все файлы
 * пользователя» не существует — Synapse не индексирует вложения. Мы
 * видим только то, что клиент уже вытянул sync'ом (initialSyncLimit: 8
 * + история открытых комнат). Поэтому:
 *  - это НЕ полный архив: старый файл из неоткрытой комнаты сюда не
 *    попадёт, и UI обязан это сказать (см. ProfileView);
 *  - догрузка истории всех 109 комнат ради списка — НЕ делаем: это
 *    сотни запросов по кнопке открытия раздела.
 *
 * Разделение «мои» / «со мной поделились» — по senderId === мой MXID.
 *
 * @param mine true — только мои вложения; false — только чужие.
 */
export function collectFiles(
  client: MatrixClient,
  mine: boolean,
): SharedFile[] {
  const me = client.getUserId();
  if (!me) return [];
  const out: SharedFile[] = [];

  for (const room of client.getRooms()) {
    if (room.getMyMembership() !== "join") continue;
    // Пространства — не чаты: файлов в их таймлайнах по определению нет,
    // но обходить их события — бесплатная защита от будущих сюрпризов.
    if (room.isSpaceRoom()) continue;
    const roomName = room.name || room.roomId;
    for (const ev of room.getLiveTimeline().getEvents()) {
      collectFromEvent(ev, me, mine, room.roomId, roomName, out);
    }
  }

  out.sort((a, b) => b.ts - a.ts);
  return out;
}

function collectFromEvent(
  ev: MatrixEvent,
  me: string,
  mine: boolean,
  roomId: string,
  roomName: string,
  out: SharedFile[],
): void {
  if (ev.getType() !== EventType.RoomMessage) return;
  // Правленные сообщения: берём актуальную замену, иначе покажем стёртый
  // файл, который в ленте уже не виден.
  const effective = ev.replacingEvent() ?? ev;
  const content = effective.getContent() as {
    msgtype?: string;
    body?: string;
    filename?: string;
    url?: string;
    info?: { mimetype?: string; size?: number; thumbnail_url?: string };
  };
  const msgtype = content.msgtype ?? "";
  if (!FILE_MSGTYPES.has(msgtype)) return;
  // Зашифрованное вложение (url нет, есть file) мы не читаем — crypto
  // выключен намеренно (README). Пропускаем, а не рисуем мусор.
  if (typeof content.url !== "string" || !content.url.startsWith("mxc://")) return;
  const senderId = ev.getSender() ?? "";
  const isMine = senderId === me;
  if (isMine !== mine) return;
  const eventId = ev.getId();
  if (!eventId || eventId.startsWith("~")) return;

  const thumb = content.info?.thumbnail_url;
  out.push({
    eventId,
    roomId,
    roomName,
    senderId,
    senderName: senderId,
    ts: ev.getTs() ?? 0,
    name: typeof content.body === "string" && content.body ? content.body : tr("timeline.attachmentFallback"),
    filename:
      typeof content.filename === "string" && content.filename
        ? content.filename
        : null,
    mxc: content.url,
    thumbMxc:
      typeof thumb === "string" && thumb.startsWith("mxc://") ? thumb : null,
    mimetype: content.info?.mimetype ?? "application/octet-stream",
    size: typeof content.info?.size === "number" ? content.info.size : null,
    kind: kindOf(msgtype),
  });
}
